import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardMedia,
  CardActionArea,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
  Paper,
} from "@mui/material";
import {
  Search,
  Close,
  CheckBoxOutlineBlank,
  CheckBox,
} from "@mui/icons-material";
import { useDebounce } from "../hooks/useDebounce";

// Debounce hook

// Mock API function - replace with your actual API call
const fetchImages = async (searchQuery = "") => {
  await new Promise((resolve) => setTimeout(resolve, 500));
  const mockImages = [
    {
      id: 1,
      previewUrl: "https://picsum.photos/200/300?random=1",
      fileName: "landscape-1.jpg",
    },
    {
      id: 2,
      previewUrl: "https://picsum.photos/200/300?random=2",
      fileName: "portrait-1.jpg",
    },
    {
      id: 3,
      previewUrl: "https://picsum.photos/200/300?random=3",
      fileName: "nature-1.jpg",
    },
    {
      id: 4,
      previewUrl: "https://picsum.photos/200/300?random=4",
      fileName: "city-1.jpg",
    },
    {
      id: 5,
      previewUrl: "https://picsum.photos/200/300?random=5",
      fileName: "animal-1.jpg",
    },
    {
      id: 6,
      previewUrl: "https://picsum.photos/200/300?random=6",
      fileName: "landscape-2.jpg",
    },
    {
      id: 7,
      previewUrl: "https://picsum.photos/200/300?random=7",
      fileName: "portrait-2.jpg",
    },
    {
      id: 8,
      previewUrl: "https://picsum.photos/200/300?random=8",
      fileName: "nature-2.jpg",
    },
  ];
  if (searchQuery) {
    return mockImages.filter((image) =>
      image.fileName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }
  return mockImages;
};

const ImageSelectionModal = ({ open, onClose, onImagesSelected }) => {
  const [images, setImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Load images
  const loadImages = useCallback(async (query = "") => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchImages(query);
      setImages(data);
    } catch (err) {
      setError("Failed to load images");
      console.error("Error loading images:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load images on mount and when search query changes
  useEffect(() => {
    if (open) {
      loadImages(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, open, loadImages]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedImages(new Set());
      setSearchQuery("");
      setError("");
    }
  }, [open]);

  const handleImageToggle = (imageId, event) => {
    // Prevent event bubbling to avoid triggering the CardActionArea click
    if (event) {
      event.stopPropagation();
    }

    setSelectedImages((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(imageId)) {
        newSelected.delete(imageId);
      } else {
        newSelected.add(imageId);
      }
      return newSelected;
    });
  };

  const handleCardClick = (imageId) => {
    setSelectedImages((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(imageId)) {
        newSelected.delete(imageId);
      } else {
        newSelected.add(imageId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedImages(new Set(images.map((img) => img.id)));
    } else {
      setSelectedImages(new Set());
    }
  };

  const handleOk = () => {
    const selectedImageObjects = images.filter((img) =>
      selectedImages.has(img.id)
    );
    onImagesSelected(selectedImageObjects);
    onClose();
  };

  const selectedCount = selectedImages.size;
  const allSelected = images.length > 0 && selectedCount === images.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: "80vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography ml={1} variant="h6" fontWeight="600">
            Select Images
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 4 }}>
        {/* Search and Controls */}
        <Paper elevation={0}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search images by filename..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Search sx={{ color: "text.secondary", mr: 1 }} />
                ),
              }}
              size="small"
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  onChange={handleSelectAll}
                  icon={<CheckBoxOutlineBlank />}
                  checkedIcon={<CheckBox />}
                />
              }
              label={`Select all (${selectedCount} selected)`}
            />

            {selectedCount > 0 && (
              <Chip
                label={`${selectedCount} selected`}
                color="primary"
                variant="filled"
              />
            )}
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Images Grid */}
        <Box sx={{ overflow: "auto", mt: 1, height: "calc(100% - 76px)" }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={200}
            >
              <CircularProgress />
            </Box>
          ) : images.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Typography color="text.secondary">
                {searchQuery
                  ? "No images found matching your search"
                  : "No images available"}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {images.map((image) => (
                <Grid item xs={6} sm={4} md={3} key={image.id}>
                  <Card
                    sx={{
                      position: "relative",
                      border: selectedImages.has(image.id) ? 2 : 1,
                      borderColor: selectedImages.has(image.id)
                        ? "primary.main"
                        : "divider",
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardActionArea onClick={() => handleCardClick(image.id)}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={image.previewUrl}
                        alt={image.fileName}
                        sx={{ objectFit: "cover" }}
                      />

                      {/* Selection Checkbox - Now properly handling clicks */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          borderRadius: 1,
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent card click when clicking checkbox area
                      >
                        <Checkbox
                          checked={selectedImages.has(image.id)}
                          onChange={(e) => handleImageToggle(image.id, e)}
                          color="primary"
                          sx={{ p: 0.5 }}
                        />
                      </Box>

                      {/* Filename */}
                      <Box sx={{ p: 1 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          title={image.fileName}
                          sx={{ fontWeight: 500 }}
                        >
                          {image.fileName}
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleOk}
          variant="contained"
          disabled={selectedCount === 0}
        >
          Select ({selectedCount})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Usage example component
const ImageSelectorExample = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  const handleImagesSelected = (images) => {
    setSelectedImages(images);
    console.log("Selected images:", images);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Button
        variant="contained"
        onClick={() => setModalOpen(true)}
        sx={{ mb: 2 }}
      >
        Select Images
      </Button>

      {selectedImages.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Selected Images ({selectedImages.length}):
          </Typography>
          <Grid container spacing={1}>
            {selectedImages.map((image) => (
              <Grid item key={image.id}>
                <Chip
                  label={image.fileName}
                  onDelete={() => {}}
                  variant="outlined"
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <ImageSelectionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onImagesSelected={handleImagesSelected}
      />
    </Box>
  );
};

export default ImageSelectorExample;
